import { config } from '../config.js';
import { readConsent, writeConsent, type ConsentChoice } from './consent.js';

/**
 * Google Analytics 4, behind Consent Mode v2.
 *
 * Everything here is inert unless `VITE_GA_MEASUREMENT_ID` was set at build
 * time. That is the kill switch and the reason a local `npm run dev`, a test
 * run and any stage deployed without the id all behave exactly as the product
 * did before analytics existed — no script, no dataLayer, no requests. It
 * follows `googleEnabled` in config.ts, which solves the same problem for
 * federation: a capability the bundle can only use where the deploy supplied
 * the thing it needs.
 *
 * **The consent default is pushed before the script tag, and the ordering is
 * the whole mechanism.** Consent Mode is not a wrapper around gtag; it is a
 * state gtag reads as it initialises. A default that arrives after the library
 * has loaded arrives after it has already decided whether it may write
 * `_ga`, so the cookie is set and the consent is decoration. Everything the
 * defaults need is therefore pushed synchronously in `start()`, before the
 * `<script>` is appended.
 *
 * The advertising signals are denied unconditionally and never granted. This
 * property sells the absence of advertising, and Consent Mode v2 requires them
 * to be *declared*, not merely omitted — leaving them out means gtag applies
 * its own defaults rather than ours.
 *
 * With `analytics_storage` denied, GA4 still sends cookieless pings: a page
 * view is counted, nothing is stored, and no visitor is followed between
 * pages. That is what an un-answered banner leaves running, and it is what a
 * voter's phone sends for the length of a live poll — see ConsentBanner for
 * why that room is never interrupted to ask.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Denied until answered, and for advertising, denied permanently. */
const DEFAULT_CONSENT = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
} as const;

let started = false;

/** Whether this build ships analytics at all. */
export const analyticsEnabled = (): boolean => config.gaMeasurementId !== '';

/**
 * The `arguments` object, not an array.
 *
 * gtag.js reads what lands on `dataLayer` as an arguments object and a real
 * Array does not always survive the trip. The official snippet uses a
 * `function` declaration for exactly this reason, so this one does too — a
 * rest parameter would be tidier and would push the wrong shape.
 */
function pushToDataLayer() {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments);
}

/**
 * Loads gtag.js with the stored consent applied, once.
 *
 * Safe to call repeatedly: the app root calls it on mount, and React 18's
 * StrictMode calls that mount twice in development. A second load would give
 * the page two GA instances and double every count.
 */
export function start(): void {
  if (started || !analyticsEnabled()) return;
  started = true;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = pushToDataLayer;

  // Order matters, and this is the order: defaults, then any stored answer,
  // then the library, then config.
  window.gtag('consent', 'default', DEFAULT_CONSENT);

  const stored = readConsent();
  if (stored === 'granted') window.gtag('consent', 'update', { analytics_storage: 'granted' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.gaMeasurementId)}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  // `send_page_view: false` because useAnalytics fires the first one itself.
  // Left on, gtag counts the landing page here and the hook counts it again a
  // tick later, and every session begins with one phantom view.
  window.gtag('config', config.gaMeasurementId, { send_page_view: false });
}

/** Applies and remembers an answer to the banner. */
export function setConsent(choice: ConsentChoice): void {
  writeConsent(choice);
  if (!analyticsEnabled()) return;
  window.gtag?.('consent', 'update', {
    analytics_storage: choice === 'granted' ? 'granted' : 'denied',
  });
}

/**
 * Counts one view of `path`.
 *
 * The path is passed explicitly rather than left to gtag's own reading of
 * `location`: on a client-side navigation gtag would read whatever the URL was
 * when the library initialised, which on every route change but the first is
 * the wrong page.
 */
export function trackPageView(path: string): void {
  if (!analyticsEnabled()) return;
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

/** Test seam: forgets that `start` ran. Not used by the app. */
export function resetForTests(): void {
  started = false;
  delete window.dataLayer;
  delete window.gtag;
}
